import { z as zod } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Stack,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import axiosInstance from 'src/utils/axios';

import { Form } from 'src/components/hook-form';
import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

const ReportBugSchema = zod.object({
  description: zod
    .string()
    .min(1, { message: 'Açıklama zorunludur!' })
    .max(1000, { message: 'Açıklama 1000 karakterden uzun olamaz!' }),
});

type ReportBugSchemaType = zod.infer<typeof ReportBugSchema>;

function ReportBug() {
  const boolean = useBoolean();

  const methods = useForm<ReportBugSchemaType>({
    resolver: zodResolver(ReportBugSchema),
    defaultValues: {
      description: '',
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const description = watch('description');
  const characterCount = description?.length || 0;

  const onSubmit = handleSubmit(async (data) => {
    try {
      // Handle form submission here
      await axiosInstance.post('/feedback', data);

      toast.success('Hata bildirildi, teşekkürler!');

      setValue('description', ''); // Clear the description field after submission, state still holds the value cause modal is gonna still mounted
      boolean.onFalse();
    } catch (error) {
      toast.error(
        'Hata bildirme işlemi sırasında bir hata oluştu, lütfen daha sonra tekrar deneyiniz!'
      );
      console.error(error);
    }
  });

  return (
    <>
      <Button
        variant="soft"
        color="error"
        startIcon={<Iconify icon="mdi:bug" />}
        fullWidth
        onClick={boolean.onTrue}
      >
        Hata Bildir
      </Button>

      <Dialog
        open={boolean.value}
        onClose={boolean.onFalse}
        maxWidth="sm"
        PaperProps={{ sx: { bgcolor: 'background.neutral' } }}
        fullWidth
      >
        <DialogTitle>Hata Bildirme Sistemi</DialogTitle>
        <Form methods={methods} onSubmit={onSubmit}>
          <DialogContent>
            <Stack>
              <Typography variant="subtitle2">
                Restoranınızda veya sistemimizde karşılaştığınız hataları bize bildirin.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Hatanın nasıl oluştuğunu açıklayın..."
                sx={{ mt: 1 }}
                error={!!errors.description}
                helperText={errors.description?.message || `${characterCount}/1000 karakter`}
                {...methods.register('description')}
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button variant="soft" onClick={boolean.onFalse}>
              Kapat
            </Button>
            <Button variant="contained" type="submit">
              Gönder
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
}

export default ReportBug;
